import _ from 'lodash';
import {
  Annotation,
  AnnotationAttachment,
  AnnotationLocation,
  AnnotationThread,
  CollectionScope,
  CommentId,
  DeleteAnnotationsMessage,
  DeleteAnnotationsQuery,
  DeleteAnnotationsResponse,
  FuidRrnInfo,
  GetCommentsUnreadCountMessage,
  GetCommentsUnreadCountQuery,
  GetCommentsUnreadCountResponse,
  MarkCommentAsReadMessage,
  MarkCommentAsReadQuery,
  MarkCommentAsReadResponse,
  RepoItem,
  ResolveAnnotationsMessage,
  ResolveAnnotationsQuery,
  ResolveAnnotationsResponse,
  SaveAnnotationsMessage,
  SaveAnnotationsQuery,
  SaveAnnotationsResponse,
  SpatialLocation,
  TemporalLocation
} from './../../autogenerated/ZoomBridge';
import GetAnnotations from './GetAnnotations';
import FileInfoUtil from './../../util/FileInfoUtil';
import ErrorId from './../../protocol/ErrorId';
import Authenticator from './../../protocol/Authenticator';
import Store from '../../store/Store';
import UnreadCommentsData from './UnreadCommentsData';
import t from '../../i18n/I18n';
import { CommentFilterOption } from '../../util/Constants';
import loginRequired from '../../auth/loginRequired';

const debug = require('../../debug/debug')('wc:util:AnnotationUtil');

export default class AnnotationUtil {
  static COMMENT = 'COMMENT';
  static NOTE = 'NOTE';
  static PRIVATE_COMMENT = 'PRIVATE_COMMENT';
  static PRIVATE_NOTE = 'PRIVATE_NOTE';
  static APPROVED = 'APPROVED';
  static REJECTED = 'REJECTED';
  static REVIEW = 'REVIEW';

  /**
   *
   * @param {string} annotationKind
   */
  static toAnnotationKindKeyFromLabel(annotationKind) {
    switch (annotationKind.toLowerCase()) {
      case 'private note':
        return AnnotationUtil.PRIVATE_NOTE;
      case 'private comment':
        return AnnotationUtil.PRIVATE_COMMENT;
      case 'comment':
        return AnnotationUtil.COMMENT;
      case 'note':
        return AnnotationUtil.NOTE;
      default:
        return annotationKind;
    }
  }

  /**
   * Creates the new instance
   * @param  {number} fuid
   * @param  {number} bid
   * @param  {number} rrn
   * @returns {FuidRrnInfo}
   */
  static createFuidRrnInfo(fuid, bid, rrn) {
    return FileInfoUtil.toFuidRrnInfo(fuid, bid, rrn);
  }

  static toFuidRrnInfo(fuid, bid, rrn) {
    return AnnotationUtil.createFuidRrnInfo(fuid, bid, rrn);
  }

  /**
   *
   * @param {RepoItem} repoItem
   */
  static repoItemToFuidRrnInfo(repoItem) {
    return FileInfoUtil.createFuidRrnInfo(repoItem);
  }

  /**
   * @param {GetAnnotations} getAnnotations
   */
  static getLastReceivedRrn(getAnnotations) {
    return getAnnotations ? getAnnotations.getLastReceivedRrn() : undefined;
  }

  /**
   * Returns the available annotations for fiven Fuid, bid rrn.
   * @param  {FuidRrnInfo} fuidRrnInfo Asset scope for comments.
   * @param {number} collectionId
   * @param {number} lastReceivedRrn RRN at which last annotation was received. If this is given, then only new annotations are returned.
   * @param {Function.<GetAnnotations>} onResult Called when results are received or any error has occurred..
   * @param {string} annotationKind
   **/
  static getAnnotations(fuidRrnInfo, collectionId, lastReceivedRrn, onResult, annotationKind = undefined) {
    /**
     * @type {GetAnnotations}
     */
    const getAnnotations = new GetAnnotations(
      fuidRrnInfo,
      collectionId,
      lastReceivedRrn,
      (me) => {
        //const annotationThreads = getAnnotations.getAnnotations();
        if (onResult) {
          onResult(getAnnotations);
        }
      },
      annotationKind
    );
  }

  /***
   *
   * @param {string} annotationThread
   * @returns {string}
   */
  static getCreatorOfThread(annotationThread) {
    const rootAnnotation = AnnotationUtil.getRootAnnotation(annotationThread);
    if (rootAnnotation) {
      const creator = rootAnnotation.getCreator();
      return creator;
    }
    return undefined;
  }

  /**
   *
   * @param {AnnotationThread} annotationThread
   *
   */
  static getAnnotationKind(annotationThread) {
    if (annotationThread) {
      return annotationThread.getAnnotationKind();
    }
    return undefined;
  }

  /**
   * @param {AnnotationThread} annotationThread
   * returns {boolean} Return true, if given thread is of comment type.
   */
  static isComment(annotationThread) {
    const kind = AnnotationUtil.getAnnotationKind(annotationThread);
    if (kind) {
      return kind === AnnotationUtil.COMMENT;
    }
    return false;
  }

  /**
   * @param {AnnotationThread} annotationThread
   * returns {boolean} Return true, if given thread is of note type.
   */
  static isNote(annotationThread) {
    const kind = AnnotationUtil.getAnnotationKind(annotationThread);
    if (kind) {
      return kind === AnnotationUtil.NOTE;
    }
    return false;
  }

  /**
   * @param {AnnotationThread} annotationThread
   * @return {boolean} Return true, if given thread is of private comment type.
   */
  static isPrivateComment(annotationThread) {
    const kind = AnnotationUtil.getAnnotationKind(annotationThread);
    if (kind) {
      return kind === AnnotationUtil.PRIVATE_COMMENT;
    }
    return false;
  }

  /**
   * @param {AnnotationThread} annotationThread
   * @return {boolean} Return true, if given thread is of private comment type.
   */
  static isPrivateNote(annotationThread) {
    const kind = AnnotationUtil.getAnnotationKind(annotationThread);
    if (kind) {
      return kind === AnnotationUtil.PRIVATE_NOTE;
    }
    return false;
  }

  /**
   * @param {AnnotationThread} annotationThread
   * @return {boolean} Return true, if given thread marked as resolved
   */
  static isResolved(annotationThread) {
    return annotationThread && annotationThread.isResolved();
  }

  /**
   * @param {AnnotationThread} annotationThread
   * @return {boolean} Return true, if given thread marked as archived
   */
  static isArchived(annotationThread) {
    return annotationThread && annotationThread.isArchived();
  }

  /**
   * A thread is a spot and could have many replies in it.
   * @param {AnnotationThread} annotationThread
   * @return {number} Return a number of how many comments are present in this thread (including root).
   */
  static getReplyCounts(annotationThread) {
    if (annotationThread && annotationThread.getAnnotation()) {
      const replies = annotationThread.getAnnotation().getReplies();
      if (replies) return _.size(replies) + 1;
      else return 1;
    }
    return 0;
  }

  /**
   * A thread is a spot and could have many replies in it.
   * All the replies except the root are returned.
   * @param {AnnotationThread} annotationThread
   * @param {boolean} includeRoot
   * @return {Array.<Annotation>} Returns an array of Annotation object(thread) which contains comment, location information etc... etc.. or empty array
   */
  static getReplies(annotationThread, includeRoot = false) {
    let annotation = annotationThread.getAnnotation();
    let values;
    if (annotation) {
      if (includeRoot) {
        values = [annotation];
        if (_.size(annotation.getReplies()) > 0) {
          values.push(...annotation.getReplies());
        }
      } else {
        const replies = annotation.getReplies();
        if (replies) {
          values = annotation.getReplies();
        }
      }
    }
    return values ? values : [];
  }

  /**
   * Check and get the first annotation. The first annotation is called the Root Annotation
   * @param {AnnotationThread} annotationThread
   * @return {Annotation} Returns an Annotation object(annotationThread) which contains comment, location information etc... etc.. or undefined
   */
  static getRootAnnotation(annotationThread) {
    if (annotationThread && annotationThread.getAnnotation()) {
      return annotationThread.getAnnotation();
    }
    return undefined;
  }

  /**
   * For video/animation/page number, get the temporal location.
   * @param {AnnotationThread} annotationThread
   * @returns {TemporalLocation} If it is video, then TemporalLocation can be used to get the offset at which comment is made.
   */
  static getTemporalLocation(annotationThread, createNewIfNotExist = false) {
    if (annotationThread) {
      /**
       * @type {AnnotationLocation}
       */
      let annotationLocation = annotationThread.getAnnotationLocation();
      if (createNewIfNotExist && !annotationLocation) {
        annotationLocation = new AnnotationLocation();
        annotationThread.setAnnotationLocation(annotationLocation);
      }
      if (annotationLocation) {
        /**
         * @type {TemporalLocation}
         */
        let temporalLocation = annotationLocation.getTemporalLocation();
        if (createNewIfNotExist && !temporalLocation) {
          temporalLocation = new TemporalLocation();
          annotationLocation.setTemporalLocation(temporalLocation);
        }
        return temporalLocation;
      }
    }
    return undefined;
  }

  /**
   * For video/animation/page number, get the temporal location.
   * @param {AnnotationThread} annotationThread
   * @returns {SpatialLocation} For images/multipage, SpatialLocation can be used.
   */
  static getSpatialLocation(annotationThread, createNewIfNotExist = false) {
    if (annotationThread) {
      /**
       * @type {AnnotationLocation}
       */
      let annotationLocation = annotationThread.getAnnotationLocation();
      if (createNewIfNotExist && !annotationLocation) {
        annotationLocation = new AnnotationLocation();
        annotationThread.setAnnotationLocation(annotationLocation);
      }
      if (annotationLocation) {
        /**
         * @type {TemporalLocation}
         */
        let spatialLocation = annotationLocation.getSpatialLocation();
        if (createNewIfNotExist && !spatialLocation) {
          spatialLocation = new SpatialLocation();
          annotationLocation.setSpatialLocation(spatialLocation);
        }
        return spatialLocation;
      }
    }
    return undefined;
  }

  /**
   * @param {AnnotationThread} annotationThread
   * @return {number} Time in milliseconds at which thread is created.
   */
  static getVideOffsetInMilliseconds(annotationThread) {
    /**
     * @type {TemporalLocation}
     */
    const temporalLocation = AnnotationUtil.getTemporalLocation(annotationThread);
    if (temporalLocation) {
      return temporalLocation.getTimeOffset();
    }
    return 0;
  }

  /**
   * @param {AnnotationThread} annotationThread
   * @returns {number}
   */
  static getPageNumber(annotationThread) {
    /**
     * @type {TemporalLocation}
     */
    const temporalLocation = AnnotationUtil.getTemporalLocation(annotationThread);
    if (temporalLocation) {
      return temporalLocation.getFrameNumber();
    }
    return 0;
  }

  /**
   * Width offset at which spot is added.
   * @param {AnnotationThread} annotationThread
   */
  static getWidthOffset(annotationThread) {
    const spatialLocation = AnnotationUtil.getSpatialLocation(annotationThread);
    if (spatialLocation) {
      return spatialLocation.getWidthOffset();
    }
    return 0;
  }

  /**
   * Height offset at which commet is added.
   * @param {AnnotationThread} annotationThread
   */
  static getHeightOffset(annotationThread) {
    const spatialLocation = AnnotationUtil.getSpatialLocation(annotationThread);
    if (spatialLocation) {
      return spatialLocation.getHeightOffset();
    }
    return 0;
  }

  /**
   * Height of asset at whcih spot is added.
   * @param {AnnotationThread} annotationThread
   */
  static getHeight(annotationThread) {
    /**
     * @type {SpatialLocation}
     */
    const spatialLocation = AnnotationUtil.getSpatialLocation(annotationThread);
    if (spatialLocation) {
      return spatialLocation.getHeight();
    }
    return 0;
  }

  /**
   * Width of asset at whcih spot is added.
   * @param {AnnotationThread} annotationThread
   */
  static getWidth(annotationThread) {
    /**
     * @type {SpatialLocation}
     */
    const spatialLocation = AnnotationUtil.getSpatialLocation(annotationThread);
    if (spatialLocation) {
      return spatialLocation.getWidth();
    }
    return 0;
  }

  /**
   *
   * Test method to print annotations details
   * @param {GetAnnotations} getAnnotations
   */
  static print(getAnnotations) {
    const annotationsThreads = getAnnotations.getAnnotations();
    /**
     * @type {Array.<string>}
     */
    const deletedAnnotationsCommentId = getAnnotations.getDeletedCommentsId();
    if (_.size(deletedAnnotationsCommentId) > 0) {
      console.log('Deleted comments id array: ', deletedAnnotationsCommentId);
    }

    if (_.size(annotationsThreads) > 0) {
      annotationsThreads.forEach((thread) => {
        /**
         * @type {AnnotationsThread}
         */
        const annotationsThread = thread;
        console.log(
          'Annotation Kind:',
          annotationsThread.getAnnotationKind,
          'COMMENT: ',
          AnnotationUtil.isComment(annotationsThread),
          'NOTE: ',
          AnnotationUtil.isNote(annotationsThread),
          'PRIVATE_COMMENT',
          AnnotationUtil.isPrivateComment(annotationsThread)
        );

        console.log('Width Offset: ', AnnotationUtil.getWidthOffset(annotationsThread));
        console.log('Height Offset: ', AnnotationUtil.getHeightOffset(annotationsThread));
        console.log('Width: ', AnnotationUtil.getWidth(annotationsThread));
        console.log('Height: ', AnnotationUtil.getHeight(annotationsThread));
        console.log(
          'Video Time in Milliseconds at which comment is posted.: ',
          AnnotationUtil.getVideOffsetInMilliseconds(annotationsThread)
        );
        console.log('Page number where comment is posted: ', AnnotationUtil.getPageNumber(annotationsThread));

        console.log('Replies count: ', AnnotationUtil.getReplyCounts(annotationsThread));

        const rootReply = AnnotationUtil.getRootAnnotation(annotationsThread);
        if (rootReply) {
          AnnotationUtil.printAnnotation(1, rootReply);
        }
        /**
         * @type {Array.<Annotation>}
         */
        const replies = AnnotationUtil.getReplies(annotationsThread);
        replies.forEach(function(reply, index) {
          /**
           * @type {Annotation}
           */
          const comment = reply;
          AnnotationUtil.printAnnotation(index + 2, comment);
        });
      });
    }

    //Procress to create new thread.
    const fuidRrnInfo = AnnotationUtil.toFuidRrnInfo(4, 1, 1);
    const collectionId = 4;
    const annotationsThread = AnnotationUtil.createNewComment(fuidRrnInfo, collectionId);
    AnnotationUtil.setWidthAndHeightOffset(annotationsThread, 50, 50);
    AnnotationUtil.appendNewReply(annotationsThread, 'First Reply');
    AnnotationUtil.saveAnnotation(fuidRrnInfo, annotationsThread, 4).then(
      (resp) => {
        /**
         * @type {SaveAnnotationsResponse}
         */
        const saveAnnotationsResponse = resp;
        /**
         * @type {Array.<AnnotationThread>}
         */
        const annotationThreads = saveAnnotationsResponse.getThreads();
        const thread = annotationThreads[0];
        AnnotationUtil.appendNewReply(thread, 'I am good!');
        AnnotationUtil.saveAnnotation(fuidRrnInfo, thread, 4);
      },
      (errorId) => {
        window.setError(errorId.toString());
      }
    );
  }

  /**
   * @private
   * @param {Annotation} comment
   * @returns {String} pretty string for an annotation
   */
  static printAnnotation(replyIndex, comment) {
    console.log(
      'Reply:',
      replyIndex,
      'From: ',
      comment.getCreator(),
      'Creation Time: ',
      comment.getCTime(),
      'Modification Time: ',
      comment.getMTime(),
      'Editor: ',
      comment.getEditor(),
      'Text',
      comment.getText()
    );
  }

  /**
   * Use this method to create new thread of comment type
   * @return {AnnotationThread}
   */
  static createNewComment() {
    const annotationThread: AnnotationThread = new AnnotationThread();
    annotationThread.setAnnotationKind(AnnotationUtil.COMMENT);
    return annotationThread;
  }

  /**
   * Use this method to create new thread of comment type and set empty text annotation
   * @return {AnnotationThread}
   */
  static createNewCommentWithText(text: string = '') {
    const annotation: Annotation = new Annotation();
    annotation.setText(text);
    annotation.setCreator(Authenticator.AUTH_DATA.username);

    const annotationThread: AnnotationThread = new AnnotationThread();
    annotationThread.setAnnotationKind(AnnotationUtil.COMMENT);
    annotationThread.setAnnotation(annotation);

    return annotationThread;
  }

  /**
   * Creates a comment with a temporal location but no spatial location.
   */
  static createAudioComment(millis: number) {
    const comment: AnnotationThread = this.createNewCommentWithText();
    AnnotationUtil.setVideoTimeOffsetInMilliseconds(comment, millis);
    return comment;
  }

  /**
   * Use this method to create new thread of note type.
   * @return {AnnotationThread}
   */
  static createNewNote() {
    const annotationsThread = new AnnotationThread();
    annotationsThread.setAnnotationKind(AnnotationUtil.NOTE);
    return annotationsThread;
  }

  static createReview() {
    const thread: AnnotationThread = new AnnotationThread();
    thread.setAnnotationKind(AnnotationUtil.REVIEW);
    AnnotationUtil.setRootReply(thread, null).setTextType(AnnotationUtil.REVIEW);
    thread.getAnnotation().setCreator(Authenticator.AUTH_DATA.username);
    thread.setAnnotationKind(AnnotationUtil.REVIEW);
    return thread;
  }

  /**
   *  Set the coordinates where comment/spot is added.
   * @param {AnnotationThread} annotationsThread
   */
  static setWidthAndHeightOffset(annotationsThread, widthOffSet, heightOffset) {
    const spatialLocation = AnnotationUtil.getSpatialLocation(annotationsThread, true);
    spatialLocation.setHeightOffset(heightOffset).setWidthOffset(widthOffSet);
  }

  /**
   * Set the source width and height. Setting this optional.
   * @param {AnnotationThread} annotationsThread
   */
  static setWidthAndHeightOfImage(annotationsThread, width, heigh) {
    const spatialLocation = AnnotationUtil.getSpatialLocation(annotationsThread, true);
    spatialLocation.setHeight(heigh).setWidth(width);
  }

  /**
   *
   * @param {AnnotationThread} annotationsThread
   * @param {number} pageNumber
   */
  static setVideoTimeOffsetInMilliseconds(annotationsThread, videoTimeInMlillis) {
    const temporalLocation = AnnotationUtil.getTemporalLocation(annotationsThread, true);
    temporalLocation.setTimeOffset(videoTimeInMlillis);
  }

  /**
   *
   * @param {AnnotationThread} annotationsThread
   * @param {number} pageNumber
   */
  static setPageNumber(annotationsThread, pageNumber) {
    const temporalLocation = AnnotationUtil.getTemporalLocation(annotationsThread, true);
    temporalLocation.setFrameNumber(pageNumber);
  }

  /**
   * Creates a new Annotion object, if not present and set the text to it.
   * Same annotation object will be returned if already existing at root without any change to it.
   * @param {AnnotationThread} annotationsThread
   * @param {String} text
   * @return {Annotation} new annotation object or existing one
   */
  static setRootReply(annotationsThread, text) {
    /**
     * @type {Annotation}
     */
    let annotation = annotationsThread.getAnnotation();
    if (!annotation) {
      annotation = new Annotation();
      annotationsThread.setAnnotation(annotation);
      annotation.setCreator(Authenticator.AUTH_DATA.username);
    }
    annotation.setText(text);
    return annotation;
  }

  /**
   * Creates a new annotation reply and sets to the thread,
   * @param {AnnotationThread} annotationsThread
   * @param {string} text
   * @return {Annotation} returns new annotation object of the reply
   */
  static appendNewReply(annotationsThread, text) {
    /**
     * @type {Annotation}
     */
    let annotation = annotationsThread.getAnnotation();
    if (!annotation) {
      //error : call setRootReply() to set the root reply.
      return AnnotationUtil.setRootReply(annotationsThread, text);
    }

    //For first comment, we store the text
    const reply = new Annotation();
    reply.setText(text);
    reply.setCreator(Authenticator.AUTH_DATA.username);
    reply.setCTime(Date.now());
    AnnotationUtil.appendReply(annotationsThread, reply);
  }

  /**
   * @private
   * @param {AnnotationThread} annotationsThread
   * @param {Annotation} annotation
   */
  static appendReply(annotationsThread, annotation) {
    let replies = AnnotationUtil.getReplies(annotationsThread);
    if (!replies) {
      replies = [];
      annotationsThread.getAnnotation().setReplies(replies);
    }
    replies.push(annotation);
  }

  /**
   * Saves the annotations on server.
   * Returns the Saved thread data; not returned in case of deletes.
   * @param {FuidRrnInfo} fuidRrnInfo
   * @param {AnnotationThread} annotationsThread
   * @param {number} collectionId
   * @param {number} contactSheetId
   */
  @loginRequired
  static saveAnnotation(fuidRrnInfo, annotationsThread, collectionId, contactSheetId = null, copyComments = false) {
    /**
     * @type {SaveAnnotationsQuery}
     */
    const saveAnnotation = new SaveAnnotationsQuery();
    /**
     * @type {SaveAnnotationsMessage}
     */
    const message = new SaveAnnotationsMessage();
    message.setCollectionScope(new CollectionScope().setCollectionId(collectionId).setContactSheetId(contactSheetId));
    message.setFuidRrnInfo(fuidRrnInfo);
    message.setThreads([annotationsThread]);
    message.setCopyComments(copyComments);
    saveAnnotation.message = message;
    return saveAnnotation.run(true);
  }

  /**
   * Deletes the given comment from server.
   * @param {number} commentId Comment Id to be deleted.
   * @param {Function.<number, ErrorId>} onResult  Function to get callback. Callback first argument would be the RRN at which deletion is done. If RRN is zero, then deletion is unsuccessful
   */
  static deleteAnnotation(commentId, onResult) {
    return AnnotationUtil.deleteAnnotations([commentId], onResult);
  }

  static testDelete() {
    AnnotationUtil.deleteAnnotation(2, function(resp, errorId) {
      console.log('Delete:', resp, errorId);
    });
  }

  /**
   * Deletes the given comments from server.
   * @param {Array.<number>} commentIds Array of comment ids
   * @param {Function.<number, ErrorId>} onResult  Function to get callback. Callback first argument would be the RRN at which deletion is done. If RRN is zero, then deletion is unsuccessful
   */
  @loginRequired
  static deleteAnnotations(commentIds, onResult) {
    const arrayOfCommentsIds = _.map(commentIds, function(commendId) {
      return new CommentId().setCommentId(commendId);
    });
    const message = new DeleteAnnotationsMessage().setDeletedComments(arrayOfCommentsIds);
    const query = new DeleteAnnotationsQuery();
    query.message = message;
    const promise = query.run(true).then(
      function(response) {
        if (response) {
          /**
           * @type {DeleteAnnotationsResponse}
           */
          const deleteAnnotationsResponse = response;
          if (onResult) {
            onResult(deleteAnnotationsResponse.getLastReceivedRrn());
          }
        }
      },
      function(errorId) {
        if (onResult) {
          onResult(undefined, errorId);
        }
      }
    );

    return onResult ? undefined : promise;
  }

  /**
   * @param {number} commentId CommentId which has to be marked as resolved or not resolved.
   * @param {boolean} resolved Pass "true" to mark it as resolved.
   * @returns {Promise.<number>} resolves with the RRN at which operation is done. If RRN is zero, then operaion is unsuccessful
   */
  @loginRequired
  static async resolve(commentId, resolved) {
    const message = new ResolveAnnotationsMessage().setCommentId(commentId).setResolved(resolved);
    const query = new ResolveAnnotationsQuery();
    query.message = message;
    const res: ResolveAnnotationsResponse = await query.run(true);
    return res ? res.getLastReceivedRrn() : 0;
  }

  /**
   *
   * @return {Promise.<GetCommentsUnreadCountResponse, ErrorId>}
   */
  static getTotalUnreadComment() {
    const message = new GetCommentsUnreadCountMessage().setFetchOnlyTotalUnreadCount(true);
    const query = new GetCommentsUnreadCountQuery();
    query.message = message;
    return query.run();
  }

  /**
   * Quite heavy backend call.
   * First call above method to get the counts and then call this method to get details.
   * @return {Promise.<*, ErrorId>}
   */
  static getUnreadCommentDetails() {
    const message = new GetCommentsUnreadCountMessage();
    message.setFetchUnreadAnnotationThread(true);
    const query = new GetCommentsUnreadCountQuery();
    query.message = message;
    return query.run();
  }

  /**
   * @param {Array.<number>} threadIds Thread id which has to be makred as read.
   * @param {number} collectionId Set the value if you want to get unread count of specific collection only.
   * @param {*} fuidRrnInfo Set the value if you want to get unread count of specific fuid with our without collection.
   * @returns {Promise.<MarkCommentAsReadResponse,ErrorId>}
   */
  @loginRequired
  static markCommentAsRead(threadIds, commentIds, collectionId = -1, fuidRrnInfo = null) {
    debug('[markCommentAsRead()] args: ', { threadIds, commentIds, collectionId, fuidRrnInfo });
    const message = new MarkCommentAsReadMessage();
    if (collectionId > 0) {
      message.setCollectionScope(new CollectionScope().setCollectionId(collectionId));
    }
    if (_.size(threadIds) > 0) {
      message.setThreadIds(threadIds);
    }
    if (_.size(commentIds) > 0) {
      message.setCommentIds(commentIds);
    }
    if (fuidRrnInfo) {
      message.setFuidRrnInfo(fuidRrnInfo);
    }
    const query = new MarkCommentAsReadQuery();
    query.message = message;
    debug('[markCommentAsRead()] running query: ', query);
    return query.run(true);
  }

  static loadAndRenderUnreadCommentDetailInWindowGlobalObject() {
    return new Promise(function(success, failure) {
      AnnotationUtil.getUnreadCommentDetails()
        .then((res: GetCommentsUnreadCountResponse) => {
          debug('[loadAndRenderUnreadCommentDetailInWindowGlobalObject()] res: ', res);
          const entries = res.getCollectionUnreadCountEntries();
          const curr: UnreadCommentsData = Store.get(UnreadCommentsData.NAME);
          const next = new UnreadCommentsData()
            .setUnreadCommentCount(res.getUnreadCount())
            .setCollectionUnreadCountEntries(entries);

          if (!next.equals(curr)) {
            debug('[loadAndRenderUnreadCommentDetailInWindowGlobalObject()] !next.equals(curr) ', { curr, next });
            Store.put(UnreadCommentsData.NAME, next);
          }

          success();
        })
        .catch(failure);
    });
  }

  /**
   * Returns the array of existing attachments on root annotation
   * @param  {AnnotationThread} annotationsThread
   * @returns {Array.<AnnotationAttachment>}
   */
  static getAttachmentsFromThread(annotationsThread) {
    if (annotationsThread) {
      /**
       * @type {Annotation}
       */
      const annotation = annotationsThread.getAnnotation();
      if (annotation) {
        return annotation.getAttachments();
      }
    }
    return undefined;
  }

  static getAttachmentFromThread(annotationsThread: AnnotationThread, index: number): AnnotationAttachment {
    const attachments: Array<AnnotationAttachment> = this.getAttachmentsFromThread(annotationsThread);
    if (_.size(attachments) > index) {
      return attachments[0];
    }
    return undefined;
  }

  /**
   * Attaches the SVG data to annotation thread.<br>
   * @param  {AnnotationThread} annotationsThread AnnotationsThread in which svg has to be saved as attachment.
   * @param  {string} svgName Name of the svg.
   * @param  {string} svgData SVG data.
   */
  static attachSvgToAnnotationThread(annotationsThread, svgName, svgData) {
    if (annotationsThread) {
      const annotation = annotationsThread.getAnnotation();
      return AnnotationUtil.attachSvgToAnnotaton(annotation, svgName, svgData);
    }
    return undefined;
  }

  /**
   * Method attaches the SVG data as attachment to an annotation.
   * Note: It is possible to attach more than on shape/svg data to an annotation.
   *
   * @param  {Annotation} annotation Annotation Object
   * @param  {string} svgName Name of the SVG, May be Circle, rectangle, etc...
   * @param  {string} svgData Actualy SVG data.
   * @param  {AnnotationAttachment} Returns the newly created AnnotationAttachment object.
   */
  static attachSvgToAnnotaton(annotation, svgName, svgData) {
    if (annotation) {
      /**
       * @type {Array.<AnnotationAttachment>}
       */
      let attachments = annotation.getAttachments();
      if (!attachments) {
        annotation.setAttachments((attachments = []));
      }
      const attachment = AnnotationUtil.createNewSvgAttachment(svgName, svgData);
      attachments.push(attachment);
      return attachment;
    }
    return undefined;
  }
  /**
   * @param  {string} name Name of the attachment. Can be shape name like: circle, rectangle, line etc..
   * @param  {string} data Actual SVG data.
   */
  static createNewSvgAttachment(name, data) {
    return AnnotationUtil.createNewAttachment(name, data, 'SVG');
  }
  /**
   * @param  {string} name Name of the attachment.
   * @param  {string} data Data for attachment.
   * @param  {string} type Enum: Can be SVG, TEXT, URL, REVIEW
   * @returns {AnnotationAttachment}
   */
  static createNewAttachment(name, data, type, id = -1) {
    return new AnnotationAttachment()
      .setData(data)
      .setDataType(type)
      .setFileName(name)
      .setCommentId(id);
  }

  static isDrawingAttachments(comment) {
    const attachments = AnnotationUtil.getAttachmentsFromThread(comment);
    if (attachments) {
      return (
        _.find(attachments, (attachment) => {
          return attachment.getDataType() === 'SVG';
        }) !== undefined
      );
    } else {
      return false;
    }
  }

  /**
   * Method can create a new AnnotationsThread and set the status in it.
   * Or it can update the status of passed annotationsThread.
   *
   * @param {string} status APPROVED or REJECTED
   * @returns {AnnotationThread}
   */
  @loginRequired
  static setReviewStatus(status, annotationsThread = new AnnotationThread()) {
    if (status && !_.includes([AnnotationUtil.APPROVED, AnnotationUtil.REJECTED], status)) {
      throw new Error('Invalid status enum passed');
    }
    const thread = annotationsThread;
    thread.setAnnotationKind(AnnotationUtil.REVIEW);
    AnnotationUtil.setRootReply(thread, status).setTextType(AnnotationUtil.REVIEW);
    return thread;
  }

  /**
   * @param  {AnnotationThread} annotationsThread
   * @returns {AnnotationThread}
   */
  static approveAnnotation(annotationsThread) {
    return AnnotationUtil.setReviewStatus(AnnotationUtil.APPROVED, annotationsThread);
  }

  /**
   * @param  {AnnotationThread} annotationsThread
   * @returns {AnnotationThread}
   */
  static rejectAnnotation(annotationsThread) {
    return AnnotationUtil.setReviewStatus(AnnotationUtil.REJECTED, annotationsThread);
  }

  /**
   *
   * @param {AnnotationThread} annotationThread
   */
  static isReviewAnnotation(annotationThread) {
    if (annotationThread && annotationThread.getAnnotationKind() === AnnotationUtil.REVIEW) {
      return true;
    }
    return false;
  }

  /**
   *
   * @param {AnnotationThread} annotationThread
   */
  static isApprovedAnnotation(annotationThread) {
    if (AnnotationUtil.isReviewAnnotation(annotationThread)) {
      return AnnotationUtil.getRootAnnotation(annotationThread).getText() === AnnotationUtil.APPROVED;
    }
    return false;
  }

  /**
   *
   * @param {AnnotationThread} annotationThread
   */
  static isRejectedAnnotation(annotationThread) {
    if (AnnotationUtil.isReviewAnnotation(annotationThread)) {
      return AnnotationUtil.getRootAnnotation(annotationThread).getText() === AnnotationUtil.REJECTED;
    }
    return false;
  }

  static isIamCreatorOfThisThread(annotationsThread: AnnotationThread) {
    const creator: string = AnnotationUtil.getCreatorOfThread(annotationsThread);
    return Authenticator.AUTH_DATA.isMe(creator);
  }

  static isIamCreatorOfThisAnnotation(annotation: Annotation) {
    const creator: string = annotation ? annotation.getCreator() : undefined;
    return Authenticator.AUTH_DATA.isMe(creator);
  }

  /**
   *
   * @param {Annotation} comment
   */
  static isReviewAnnotationComment(comment) {
    return comment && comment.getTextType() && comment.getTextType() === 'REVIEW';
  }

  static getTypesOfAnnotation() {
    return [
      { value: AnnotationUtil.COMMENT, label: t('COMMENT'), icon: 'images/comment.svg' },
      { value: AnnotationUtil.PRIVATE_NOTE, label: t('PRIVATE_NOTE'), icon: 'images/note.svg' }
    ];
  }

  static checkCommentStatus(thread: AnnotationThread, filter: string): boolean {
    /**
     * Check is comment is resolved, not resolved or all.
     * This method is used for filtering the comment list.
     */
    if (filter === CommentFilterOption.RESOLVED) {
      return thread.isResolved();
    } else if (filter === CommentFilterOption.UNRESOLVED) {
      return !thread.isResolved();
    } else if (filter === CommentFilterOption.ALL) {
      return true;
    }
    return false;
  }
}
